import { useCallback, useEffect, useRef, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useChat } from "@/contexts/ChatContext";

import ChatEmptyState from "./ChatEmptyState";
import ChatMessageBubble from "./ChatMessageBubble";
import ChatStatusLine from "./ChatStatusLine";
import ChatSuggestedReplies from "./ChatSuggestedReplies";
import ChatSystemRow from "./ChatSystemRow";
import {
  AUTHOR_PREFIX_BOT,
  AUTHOR_PREFIX_USER,
  COMPOSER_PLACEHOLDER_STREAMING,
} from "./chatCopy";
import { toolStatusLabel } from "./toolLabels";

/**
 * The scrollport, the turn loop, the autoscroll and the panel's ONE announcer.
 *
 * A PLAIN `overflow-y-auto` DIV, AND NOT THE RADIX SCROLL PRIMITIVE. The choice is load bearing rather
 * than a preference (it was Open Item 3 of the design contract and is now settled): the stick
 * threshold below is direct `scrollTop` / `scrollHeight` / `clientHeight` arithmetic on ONE element,
 * and that primitive's inner viewport wrapper puts the element one indirection away, which makes the
 * measurement a second thing to keep in step with the markup. One mechanism for the whole list; the
 * two are never nested, and nothing here should be "upgraded" to the vendored component.
 */

/**
 * The autoscroll stick threshold, in pixels from the bottom.
 *
 * Inside it the list follows the answer; outside it the visitor is READING and the list holds still.
 * `scrollHeight - scrollTop - clientHeight <= 48` is the whole rule, re-asked on every scroll so
 * coming back down inside the threshold resumes the follow.
 *
 * There is deliberately NO "jump to latest" button. It is one more control to design, position and
 * localise for a 400px panel, and the thread is short enough that scrolling back down is the cheaper
 * gesture.
 */
const STICK_THRESHOLD_PX = 48;

/**
 * The waiting-for-the-first-token row: three 6px dots, staggered.
 *
 * NO TEXT LABEL. A sentence at t=0 is noise when the answer is 400ms away, and any wording would
 * either promise a result the run has not produced or narrate our own plumbing.
 *
 * `.chat-thinking-dot` is the hook the stylesheet's reduced-motion query names: under
 * `prefers-reduced-motion` all three hold static and VISIBLE, so the row still reads as "working"
 * rather than as three stray specks. The class is applied alongside `animate-pulse` rather than the
 * query disabling `animate-pulse` globally, which would have stopped every skeleton on the site.
 */
const ThinkingDots = () => (
  <div className="flex items-center gap-1" aria-hidden="true">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="chat-thinking-dot h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--ys-ink-2)]"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);

export default function ChatMessageList() {
  const {
    rows,
    threadLoading,
    streaming,
    toolName,
    reconnecting,
    limit,
    composerLocked,
    pinToBottomToken,
    suggestedReplies,
  } = useChat();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  /** Whether the list is currently following the bottom. Starts true: a fresh thread opens pinned. */
  const pinnedRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (element === null) return;
    element.scrollTop = element.scrollHeight;
  }, []);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (element === null) return;
    pinnedRef.current =
      element.scrollHeight - element.scrollTop - element.clientHeight <= STICK_THRESHOLD_PX;
  }, []);

  // Follow the thread while pinned. Depends on `rows` by identity, which the context replaces on every
  // delta, so a streaming answer scrolls itself; and on the trailing indicators, because the status
  // line and the dots change the content height too.
  //
  // `suggestedReplies` is in the list for the same reason as the other trailing indicators, not as a
  // completeness gesture: a chip row ARRIVING adds up to three 44px buttons to the content height in
  // one commit, and the effect that follows the bottom has to re-run on that commit or the list
  // settles one row short of it and the newest chips sit below the fold.
  useEffect(() => {
    if (pinnedRef.current) scrollToBottom();
  }, [rows, toolName, streaming, reconnecting, threadLoading, suggestedReplies, scrollToBottom]);

  // THE VISITOR'S OWN SEND PINS UNCONDITIONALLY, even if they had scrolled away: they just acted and
  // they want to see it. `pinToBottomToken` changes on exactly that moment and on nothing else.
  useEffect(() => {
    pinnedRef.current = true;
    scrollToBottom();
  }, [pinToBottomToken, scrollToBottom]);

  // ─── The announcer ──────────────────────────────────────────────────────────
  //
  // ONE visually-hidden `polite` `status` node, and it announces exactly three things. The STREAMING
  // BUBBLE IS NOT A LIVE REGION: announcing token by token would fire hundreds of interruptions per
  // answer, and `assertive` would cut the visitor off mid-word. The log itself is `aria-live="off"`
  // for the same reason, which is also what stops the two from double-announcing.
  const [announcement, setAnnouncement] = useState("");

  // (c) THE COMPOSER'S LOCK TRANSITION, and only the lock. This item exists because the action
  // button's accessible name mutates in place from "send" to "stop" underneath a visitor who may be
  // focused on it: a sighted visitor gets the placeholder swap for free and a screen-reader user gets
  // nothing. Nothing is announced on UNLOCK, because (b) below already covers that moment.
  const previousLocked = useRef(false);
  useEffect(() => {
    if (composerLocked && !previousLocked.current) {
      setAnnouncement(COMPOSER_PLACEHOLDER_STREAMING);
    }
    previousLocked.current = composerLocked;
  }, [composerLocked]);

  // (a) THE TOOL-STATUS LABEL WHEN IT CHANGES. Per change, not per frame: the context holds one tool
  // at a time, so this fires once per tool rather than once per progress tick.
  const previousTool = useRef<string | null>(null);
  useEffect(() => {
    if (toolName !== null && toolName !== previousTool.current) {
      setAnnouncement(toolStatusLabel(toolName));
    }
    previousTool.current = toolName;
  }, [toolName]);

  // (b) THE FINAL COMPLETE ANSWER, once the turn terminates. This is the one place the answer's text
  // is read out, and it is read ONCE, whole, after it is finished being written.
  const previousStreaming = useRef(false);
  useEffect(() => {
    if (!streaming && previousStreaming.current) {
      for (let index = rows.length - 1; index >= 0; index -= 1) {
        const row = rows[index];
        if (row !== undefined && row.kind === "turn" && row.role === "assistant") {
          if (row.content.trim() !== "") setAnnouncement(row.content);
          break;
        }
      }
    }
    previousStreaming.current = streaming;
  }, [streaming, rows]);

  // ─── The trailing indicator ─────────────────────────────────────────────────
  //
  // ONE signal at a time. A tool line wins over the dots: it says the same "working" with a reason
  // attached. Otherwise the dots appear while a run is live and has produced no text yet (state 2)
  // and again if a reconnect has been going long enough for D-09 to say anything at all (state 7),
  // where they sit beneath the partial text and are honest -- the bot IS still working.
  const awaitingFirstToken = !rows.some((row) => row.kind === "turn" && row.streaming === true);
  const showThinkingDots = streaming && (awaitingFirstToken || reconnecting);

  // A thread can hold an expired refusal from earlier in the conversation, so only the LAST limit row
  // is the one in force, and only while the context still holds a limit. When the window passes both
  // this row and the composer lock clear silently.
  const activeLimitRowId = (() => {
    if (limit === null) return null;
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      const row = rows[index];
      if (row !== undefined && row.kind === "system" && row.system === "limit") return row.id;
    }
    return null;
  })();

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-live="off"
        data-chat-message-list="true"
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"
      >
        {threadLoading ? (
          // D-03: the SHAPE of what is loading, not the fact that something is. Three bubble-height
          // rows rather than a spinner. Historical turns get NO entry animation when they land --
          // animating twenty old bubbles in on every open is a widget tell, and this shell is meant to
          // read as part of the site.
          <div className="flex flex-col gap-4" aria-hidden="true">
            <Skeleton className="h-16 w-4/5 rounded-[var(--ys-r-md)]" />
            <Skeleton className="ms-auto h-12 w-3/5 rounded-[var(--ys-r-md)]" />
            <Skeleton className="h-20 w-11/12 rounded-[var(--ys-r-md)]" />
          </div>
        ) : rows.length === 0 ? (
          // An empty thread is the NORMAL first-open case, not a failure: it renders the greeting and
          // the chips, never an error and never a "no messages" notice.
          <ChatEmptyState />
        ) : (
          <div className="flex flex-col gap-4">
            {/* 16px between turns. Each turn is a list item carrying a VISUALLY HIDDEN author prefix,
                because alignment alone tells a screen reader nothing and colour alone fails at low
                vision. Colour, alignment and this prefix are the three author cues. */}
            <ul className="flex list-none flex-col gap-4 p-0">
              {rows.map((row) => (
                <li key={row.id} className="flex flex-col">
                  {row.kind === "turn" ? (
                    <>
                      <span className="sr-only">
                        {row.role === "user" ? AUTHOR_PREFIX_USER : AUTHOR_PREFIX_BOT}
                      </span>
                      <ChatMessageBubble turn={row} />
                    </>
                  ) : (
                    <ChatSystemRow row={row} active={row.id === activeLimitRowId} />
                  )}
                </li>
              ))}
            </ul>

            {toolName !== null ? (
              <ChatStatusLine toolName={toolName} />
            ) : showThinkingDots ? (
              <ThinkingDots />
            ) : null}

            {/* AFTER the turns and AFTER the status line, and only in this branch: an empty thread
                renders `ChatEmptyState`, whose own chips are the template set. No condition is needed
                here because the component self-suppresses on an empty array.

                The chips are deliberately NOT announced. The announcer reads the finished answer
                once, whole; reading three follow-up questions after it would be a second
                interruption for the one visitor least able to skip past it. */}
            <ChatSuggestedReplies />
          </div>
        )}
      </div>

      {/* The announcer sits OUTSIDE the log, so the log's `aria-live="off"` and this node's `polite`
          cannot be read as nested live regions. `aria-atomic` because a status label and an answer are
          each one utterance, not a diff. */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </>
  );
}
